"use client";
import React, { useEffect, useState } from "react";
import GooglePlayButton from "@/mocks/assets/footerAssets/googleplaybadge.png";
import AppStoreButton from "@/mocks/assets/footerAssets/appstorebadge.svg";
import Image from "next/image";
import CloverIcon from "../_icons/cloverIcon";
import LanguageIcon from "../languageIcon/languageIcon";
import getMerchantDetails from "@/dataLayer/getMerchantDetails";
import getPolicies from "@/dataLayer/getPolicies";
import { localeOptions } from "../_localized/localizationData";
import { usePathname } from "next/navigation";
import { MerchantFooter } from "../merchantFooter/merchantFooter";
import { Service } from "@/dto/service";

export interface FooterProps {
  termsOfServiceUrl: string;
  privacyPolicyUrl: string;
  googlePlayUrl: string;
  appStoreUrl: string;
  learnMoreUrl: string;
  languageString: string;
}

export const Footer = (props: FooterProps) => {
  const pathname = usePathname();
  const [merchant, setMerchant] = useState<any>(null);
  const [classNames, setClassNames] = useState([
    "w-full",
    "bg-[#F8F8F8]",
    "text-[#000000]",
    "flex",
    "flex-col",
    "gap-[36px]",
    "px-4",
    "sm:px-12",
    "pb-2",
  ]);
  const [showLinks, setShowLinks] = useState({
    returnsAndExchangePolicy: false,
    termsOfUsePolicy: false,
  });

  const isCheckoutPage: boolean =
    pathname.includes("/checkout") || pathname.includes("/guest-checkout");
  const isProfilePage: boolean =
    pathname.includes("/customer-profile") ||
    pathname.includes("/verify-email");
  const isConfirmationPage: boolean = pathname.includes("/confirmation");

  useEffect(() => {
    if (pathname === "/guest-checkout") {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter(
          (className) => className !== "pb-2"
        );
        return [...tempClasses, "pb-16"];
      });
    } else {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter((name) => name !== "pb-16");
        return [...tempClasses, "pb-2"];
      });
    }
  }, [pathname]);

  useEffect(() => {
    const fetchMerchantDetails = async () => {
      const merchant = await getMerchantDetails();
      setMerchant(merchant);

      if (merchant) {
        const policies = await getPolicies(merchant.id);
        setShowLinks(policies.showLinks);
      }
    };

    fetchMerchantDetails();
  }, []);

  if (!merchant) {
    return <></>;
  }

  const isSimpleStorefront = merchant.merchantMeta.selectedSetup === "Simple";
  const service = merchant.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const currentLocale = merchant.locale as keyof typeof localeOptions;
  const languageString =
    localeOptions[currentLocale]?.localizationString ?? props.languageString;
  return (
    <div className="w-full bg-[#F8F8F8]">
      {!isSimpleStorefront &&
        !isCheckoutPage &&
        !isProfilePage &&
        !isConfirmationPage && (
          <div className="flex-1 flex flex-col container w-full mx-auto">
            <MerchantFooter
              merchantName={merchant.name}
              merchantAddress={merchant.address}
              merchantPhoneNumber={merchant.phone}
              merchantEmail={merchant.customerContactEmail}
              businessHours={
                merchant.isRestaurant
                  ? service?.hours
                  : merchant?.businessHours?.hours
              }
              facebookUrl={merchant.merchantMeta.socialMediaFacebookUrl}
              instagramUrl={merchant.merchantMeta.socialMediaInstagramUrl}
              timezone={merchant.timezone}
              showLinks={showLinks}
              locale={merchant.locale}
            />
          </div>
        )}
      <div className={classNames.join(" ")}>
        <div className="w-full border-t-[1px] border-[#727272]"></div>
        <div className="flex flex-col sm:flex-row justify-between">
          <div className=" flex flex-col items-start content-center mb-6 sm:w-3/5">
            <CloverIcon />
            <div className="flex flex-row items-start mt-8 sm:mt-6">
              <div className="text-left sm:text-center">
                <span>
                  <a
                    href={`https://www.clover.com/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Powered by © {new Date().getFullYear()} Clover Network, LLC
                  </a>
                </span>
                &nbsp;&#8226;&nbsp;
                <span>
                  <a
                    href={`https://www.clover.com/consumer/terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Clover Terms
                  </a>
                </span>
                &nbsp;&#8226;&nbsp;
                <span>
                  <a
                    href={`https://www.clover.com/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Clover Privacy Policy
                  </a>
                </span>
              </div>
            </div>
            <p className="text-left font-normal">
              Clover and the Clover logo are trademarks of Clover Network, LLC.
              Other trademarks are the property of their respective owners. This
              site is protected by reCAPTCHA and the &nbsp;
              <span>
                <a
                  href={`https://policies.google.com/privacy?hl=en`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Google Privacy Policy
                </a>
              </span>
              &nbsp;and&nbsp;
              <span>
                <a
                  href={`https://policies.google.com/terms?hl=en`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Terms of Service
                </a>
              </span>
              &nbsp;apply.
            </p>
          </div>

          <div className="flex flex-col justify-start sm:justify-between items:start sm:items-end mb-6 gap-5 sm:gap-0">
            {merchant.isRestaurant && merchant.locale === "en-US" && (
              <div className="flex flex-col w-full justify-center items-start sm:items-end">
                <p className={"text-center sm:text-left"}>
                  Order ahead and earn rewards on the Clover app
                </p>
                <div className="flex flex-row w-full justify-between sm:justify-start">
                  <a
                    href={props.googlePlayUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Image
                      className="mt-3 mr-3 h-14"
                      src={GooglePlayButton.src}
                      height={10}
                      width={180}
                      alt="GooglePlayButton"
                    />
                  </a>
                  <a href={props.appStoreUrl} target="_blank" rel="noreferrer">
                    <Image
                      className="mt-3 h-14 relative sm:left-2"
                      src={AppStoreButton.src}
                      height={50}
                      width={180}
                      alt="AppStoreButton"
                    />
                  </a>
                </div>
              </div>
            )}
            <div className="text-[14px]/5 flex flex-row space-x-3 items-center">
              <LanguageIcon
                locale={merchant.locale.toLocaleLowerCase() || "en-us"}
                width="20"
                height="20"
                role="img"
                aria-label="LanguageIcon"
              />
              <p>{languageString}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
